import type { SkillContext } from "./types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Helper para narrativa visual estándar de skills.
 * Muestra step inicial → opcionalmente navega a un tab → ejecuta API →
 * step de éxito/error → cleanup.
 *
 * Uso:
 *   return withFollow(ctx, {
 *     intro: "Creando caja…",
 *     successMsg: "✓ Caja creada",
 *     restaurantId,
 *     targetTab: "cajas",
 *     run: async () => {
 *       const { data } = await apiWithAuth.post(...);
 *       return { ok: true, result: { id: data.id }, humanSummary: "..." };
 *     },
 *   });
 */
export async function withFollow<
  T extends { ok: true; result?: unknown; humanSummary?: string } | { ok: false; error: string },
>(
  ctx: SkillContext,
  opts: {
    intro: string;
    successMsg?: string;
    errorMsg?: string;
    targetTab?: string;
    targetTabLabel?: string;
    restaurantId?: string;
    run: () => Promise<T>;
  },
): Promise<T> {
  const { intro, successMsg, errorMsg, targetTab, targetTabLabel, restaurantId, run } = opts;

  if (ctx.follow.enabled) {
    await ctx.follow.step(intro, { durationMs: 600 });
    if (targetTab) {
      await ctx.follow.clickAt(`[data-copilot-tab="${targetTab}"]`, {
        durationMs: 700,
        label: targetTabLabel ?? targetTab,
      });
      if (restaurantId) {
        ctx.router.push(
          `/dashboard/restaurantes/${restaurantId}?tab=${encodeURIComponent(targetTab)}`,
        );
      }
      await sleep(900);
    }
  }

  let result: T;
  try {
    result = await run();
  } catch (err) {
    if (ctx.follow.enabled) {
      await ctx.follow.step(errorMsg ?? "✗ Error", {
        durationMs: 700,
        spinner: false,
      });
      ctx.follow.clear();
    }
    throw err;
  }

  if (ctx.follow.enabled) {
    if (result.ok && successMsg) {
      await ctx.follow.step(successMsg, {
        durationMs: 900,
        spinner: false,
      });
    } else if (!result.ok) {
      await ctx.follow.step(errorMsg ?? "✗ Error", {
        durationMs: 700,
        spinner: false,
      });
    }
    ctx.follow.clear();
  }

  return result;
}
